import { Role } from '~/utils/shared/roles'
import { ContextBase, UserBase } from './context'
import { SpaceType } from '~/packages/general/variable/spaceTime.types'
import { Feature, Polygon, MultiPolygon } from 'geojson'
import type {
  UserAccessArea,
  UserPermissionKey,
} from '~/packages/general/user/permission/userPermissionCatalog'

export interface ImageryToDownload {
  type: 'roadmap' | 'hybrid' | 'terrain'
  minZoom: number
  maxZoom: number
}

export interface GeneralUserSettings {
  preferredCoordinates: [number, number]
  preferredSpaceDelimitationType: Exclude<SpaceType, 'item' | 'custom'>
  preferredSpaceDelimitationId: number
  preferredSpaceDelimitationTitle: string
  preferredSpaceDelimitationShape?: Feature<Polygon | MultiPolygon>
  preferredSpaceChildrenType: Exclude<SpaceType, 'item' | 'custom'>
  imageryToDownload?: ImageryToDownload[]
}

export interface PublicSafetyUserSettings {}
export interface HealthUserSettings {}
export interface EducationUserSettings {}

export interface UserSettings {
  // General settings
  general: GeneralUserSettings

  // Sector specific settings
  publicSafety?: PublicSafetyUserSettings
  health?: HealthUserSettings
  education?: EducationUserSettings
}

export interface UserClient {
  userClientId?: number | null
  clientId: string
  name?: string | null
  municipalityId?: number | null
  stateId?: number | null
  countryId?: number | null
  roles: Role[]
  settings: UserSettings
}

export interface User extends UserBase {
  displayName: string | null
  email: string
  phoneNumber: string | null
  photoURL: string | null
  emailVerified: boolean
}

export interface UserProfile extends User {
  activeClient: UserClient
  availableClients?: UserClient[]
  requiresActiveClientSelection?: boolean
  effectivePermissions?: UserPermissionKey[]
  deniedPermissionKeys?: UserPermissionKey[]
  accessAreas?: UserAccessArea[]
  cpf?: string | null
  cns?: string | null
}

export interface Context extends ContextBase {
  user: UserProfile | null
}

type EntitySchemaField = {
  currentMeta?: {
    label?: string
    alternativeLabels?: string[]
  }
}

export interface Entity {
  kind: 'entity'
  key: string
  title: string
  alternativeTitles?: string[]
  schema: {
    shape: Record<string, EntitySchemaField>
  }
}

export type Entities = Entity[]

export interface PropertyBase {
  kind: 'property'
  entityKey: string
  entityAlias: string
  name: string
}

export interface OrderBase {
  kind: 'order'
  entityKey?: string
  property?: string
  direction?: 'asc' | 'desc'
}

export interface Operator {
  kind: 'operator'
  operator: string
  left: unknown
  right: unknown
}

export function equals(left: unknown, right: unknown): Operator {
  return {
    kind: 'operator',
    operator: '=',
    left,
    right,
  }
}

// Prix API
export { default as defineAction } from './actionDefinition'
export type {
  ActionDefinition,
  BaseActionDefinition,
  BaseActionDefinitionEnhanced,
  ActionDefinitionEnhanced,
  BaseTrigger,
  Trigger,
} from './actionDefinition'
export { context, user } from './context'
export type { ContextBase, ContextProperty, UserBase } from './context'
export * from './error'
export { default as errors } from './error'
export { default as array } from './types/array'
export type { ArrayType } from './types/array'
export { default as boolean } from './types/boolean'
export type { BooleanType } from './types/boolean'
export { default as date } from './types/date'
export type { DateType } from './types/date'
export { default as dateTime, timestamps } from './types/dateTime'
export type { DateTimeType } from './types/dateTime'
export { enumerated } from './types/enumerated'
export { default as interval } from './types/interval'
export { default as month } from './types/month'
export type { MonthType } from './types/month'
export { default as number } from './types/number'
export type { NumberType } from './types/number'
export { default as object } from './types/object'
export type { ObjectType, ShapeBase } from './types/object'
export { default as string } from './types/string'
export type { StringType } from './types/string'
export { default as time } from './types/time'
export type { TimeType } from './types/time'
export { default as expression } from './types/expression'
export type { ExpressionType } from './types/expression'
export { default as week } from './types/week'
export type { WeekType } from './types/week'
export { default as year } from './types/year'
export type { YearType } from './types/year'
// export { listInput, listOutput } from './types/list'
export type { default as Theme } from './utils/theme'
export { default as mapRange } from './utils/math/mapRange'
export type { AbstractType, InferNative, Type, TypeWithValue } from './types/type'
export type { Source, Sources } from './sources/source'
export * from './utils/types/boolean'
export * from './utils/types/number'
export * from './utils/types/set'
export * from './utils/types/string'
export * from './utils/typing'
